import { useMutation } from '@tanstack/react-query';
import {
  addressAutocomplete,
  geocode,
  reverseGeocode,
} from '../services/maps-api';
import { useLocationStore } from '../store/useLocationStore';
import { useAuthStore } from '../store/useAuthStore';
import type { Address } from '../types/address';

/**
 * @param types Google Places `types` filter. Omitted (the default) returns
 *   street addresses — what the address pickers want. `PLACE_TYPES_REGIONS`
 *   returns cities / neighborhoods / counties / states, which is what the
 *   locale-search surfaces want.
 */
export function useAddressAutocomplete({ types }: { types?: string } = {}) {
  return useMutation({
    mutationFn: ({ searchText, sessionToken }: { searchText: string; sessionToken?: string }) => {
      // Bias autocomplete by the user's resolved locale (the thing chosen via
      // the top-bar location selector). Read from the store at call time so
      // every keystroke uses the freshest value — picking a new locale takes
      // effect immediately without resubscribing the mutation.
      const locale = useLocationStore.getState().locale;
      const bias =
        locale?.lat != null && locale?.lon != null
          ? { lat: locale.lat, lon: locale.lon, radius: locale.radius }
          : undefined;
      // The autocomplete endpoint is JWT-gated. Logged-out callers (the
      // logged-out publish flow's LocationPicker / AddressEditCard) reach it
      // with the web `listing_generate` service key; authed callers use their
      // session. Resolved here so every call site is covered automatically.
      const serviceKey = useAuthStore.getState().isAuthenticated
        ? undefined
        : (import.meta.env.VITE_GENERATE_SERVICE_KEY as string | undefined);
      return addressAutocomplete(searchText, { sessionToken, bias, serviceKey, types });
    },
  });
}

export function useGeocode() {
  return useMutation({
    mutationFn: (searchText: string) => geocode(searchText),
  });
}

/**
 * Resolve free text — a typed ZIP, or a city/region description picked from
 * region autocomplete — to a full Address.
 *
 * A ZIP geocodes straight to a `postal_code` result and this is just
 * `useGeocode`. A city geocodes to a *locality* result, whose address
 * components carry no `postal_code` at all — and postal code is what every
 * downstream consumer needs (`isSufficientLocation`, the `/locale` resolve,
 * Stripe tax on a profile address). So when the forward geocode comes back
 * without one, we reverse-geocode the returned centroid to fill it in.
 *
 * NOTE the seam that creates: for a city pick, the postal code is the city
 * centroid's, not the user's. That is exactly right for a browse locale (the
 * feed anchors on the centroid either way) and it is an approximation for a
 * saved profile address — good enough for the orderer minimum, which is why
 * publish still demands a real street via `isFullProfileAddress`.
 */
export function useResolvePlace() {
  return useMutation({
    mutationFn: async (searchText: string): Promise<Address> => {
      const address = await geocode(searchText);
      if (address.postalCode || address.lat == null || address.lon == null) {
        return address;
      }
      try {
        const centroid = await reverseGeocode(address.lat, address.lon);
        return {
          ...address,
          postalCode: centroid.postalCode,
          city: address.city || centroid.city,
          stateProvince: address.stateProvince || centroid.stateProvince,
          country: address.country || centroid.country,
        };
      } catch {
        // Non-fatal: hand back the postal-less address and let the caller's
        // own sufficiency check produce the user-facing error.
        return address;
      }
    },
  });
}

export function useReverseGeocode() {
  return useMutation({
    mutationFn: ({ lat, lon }: { lat: number; lon: number }) =>
      reverseGeocode(lat, lon),
  });
}
